import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLang } from "../i18n/LanguageContext";
import { useStore } from "../context/StoreContext";

export const Header = () => {
  const { t } = useLang();
  const { cartCount, setCartOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/shop", label: t("nav.shop"), testid: "header-nav-shop-link" },
    { to: "/advisor", label: t("nav.advisor"), testid: "header-nav-advisor-link" },
    { to: "/simulator", label: t("nav.simulator"), testid: "header-nav-simulator-link" },
    { to: "/bundles", label: t("nav.bundles"), testid: "header-nav-bundles-link" },
    { to: "/compare", label: t("nav.compare"), testid: "header-nav-compare-link" },
    { to: "/wholesale", label: t("nav.wholesale"), testid: "header-nav-wholesale-link" },
  ];

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/shop?search=${encodeURIComponent(q.trim())}`); setOpen(false); }
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-transparent"}`}>
      <div className="container-lumeo flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`px-3 py-2 rounded-lg text-sm transition-colors ${loc.pathname === l.to ? "text-cyanx" : "text-foreground/75 hover:text-foreground"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <form onSubmit={submitSearch} className="hidden md:flex items-center">
            <div className="flex items-center gap-2 rounded-full px-3 py-2 hairline focus-within:shadow-glow-cyan transition-shadow">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("common.search")} className="bg-transparent outline-none text-sm w-28 xl:w-40 placeholder:text-muted-foreground" />
            </div>
          </form>
          <LanguageSwitcher />
          <button data-testid="header-cart-button" onClick={() => setCartOpen(true)}
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full hairline hover:shadow-glow-cyan transition-shadow">
            <ShoppingBag className="h-5 w-5" />
            {cartCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-cyanx text-[#04121a] border-0 justify-center">{cartCount}</Badge>
            )}
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="header-mobile-menu" className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full hairline">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="glass border-border w-[85vw] sm:w-96">
              <form onSubmit={submitSearch} className="mt-8 mb-4 flex items-center gap-2 rounded-full px-3 py-2 hairline">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.searchPlaceholder")}
                  className="bg-transparent outline-none text-sm flex-1" />
              </form>
              <div className="flex flex-col gap-1">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    className="px-3 py-3 rounded-lg text-base text-foreground/85 hover:bg-white/5">{l.label}</Link>
                ))}
                <Link to="/accessories" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base text-foreground/85 hover:bg-white/5">{t("nav.accessories")}</Link>
                <Link to="/contact" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base text-foreground/85 hover:bg-white/5">{t("nav.contact")}</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
